import { Extent } from "ol/extent";
import { FeatureLike } from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import LayerGroup from "ol/layer/Group";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import { all as allStrategy, bbox as bboxStrategy } from "ol/loadingstrategy";
import { EXTENT } from "ol/proj/epsg3857";
import Projection from "ol/proj/Projection";
import { Cluster, WMTS } from "ol/source";
import OSMSource from "ol/source/OSM";
import TileWMS from "ol/source/TileWMS";
import VectorSource from "ol/source/Vector";
import { Fill, Icon, Style, Text } from "ol/style";
import Stroke from "ol/style/Stroke";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import { useCallback } from "react";
import { useThirdPartyApi } from "~/api/GeneratedApi";
import oceanIcon from "~/assets/img/ocean.svg";
import wavesIcon from "~/assets/img/waves.svg";
import windIcon from "~/assets/img/wind.svg";
import { useAccess } from "~/utils/hooks/useAccess";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import useAuth from "~/utils/hooks/useAuth";
import { createAISVesselLayer } from "../functions/createAISVesselLayer";
import { createArrivalZoneLayer } from "../functions/createArrivalZoneLayer";
import createAtoNLayer from "../functions/createAtoNLayer";
import createBerthLayer from "../functions/createBerthLayer";
import createDamLayer from "../functions/createDamLayer";
import createEventLayer from "../functions/createEventLayer";
import createFairwayLayer from "../functions/createFairwayLayer";
import createFerryLayer from "../functions/createFerryLayer";
import { createIceLayer } from "../functions/createIcelayer";
import { createLayer } from "../functions/createLayer";
import { createLayerGroup } from "../functions/createLayerGroup";
import { createLuodeLayer } from "../functions/createLuodeLayer";
import createMeasurementsLayer from "../functions/createMeasurementsLayer";
import { createNauticalWarningLayer } from "../functions/createNauticalWarningLayer";
import createPortLayer from "../functions/createPortLayer";
import { createPortServiceLayer } from "../functions/createPortServiceLayer";
import {
  createProjectLayer,
  createProjectPlanningLayer,
  createProjectPoiItemLayer,
} from "../functions/createProjectLayer";
import { createTaivalTrackerLayer } from "../functions/createTaivalTrackerLayer";
import { createVehicleEquipmentsLayer } from "../functions/createVehicleEquipmentLayer";
import { createYepzonTrackerLayer } from "../functions/createYepzonTrackerLayer";
import { ObservableProperties } from "../types/fmi";
import { LayerGroupTitle, LayerTitle } from "../types/layers";
import { SeaMapItem, WaveMapItem, WindMapItem } from "../types/mapItems";
import { useWeatherLayerHelpers } from "./useWeatherLayerHelpers";

interface UseCreateMapLayersReturn {
  createMapLayers: (includedLayers?: LayerTitle[]) => LayerGroup[];
}

export const vaylaAttributions = "Väylävirasto / latauspalvelu, CC 4.0 BY.";

export const fmiAttributions = "Ilmatieteen laitos / latauspalvelu, CC 4.0 BY.";

export const digitrafficAttributions = "Fintraffic / digitraffic.fi, CC 4.0 BY";

const shoreWmtsOptions = {
  url: "https://julkinen.traficom.fi/rasteripalvelu/wmts?",
  layer: "Traficom:Rannikkokartat public",
  format: "image/png",
  matrixSet: "WGS84_Pseudo-Mercator",
  projection: new Projection({
    code: "EPSG:3857",
    units: "m",
    extent: [
      -20037508.342789244, -20037508.342789244, 20037508.342789244,
      20037508.342789244,
    ],
    worldExtent: [-180, -85, 180, 85],
    axisOrientation: "enu",
    global: true,
  }),
  requestEncoding: "KVP",
  tileGrid: new WMTSTileGrid({
    origins: [
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
      [-20037508.342789248, 20037508],
    ],
    resolutions: [
      4891.96981025128, 2445.98490512564, 1222.99245256282, 611.49622628141,
      305.7481131407048, 152.8740565703525, 76.43702828517624,
      38.21851414258813, 19.10925707129406, 9.554628535647032,
      4.777314267823516,
    ],
    matrixIds: [
      "WGS84_Pseudo-Mercator:5",
      "WGS84_Pseudo-Mercator:6",
      "WGS84_Pseudo-Mercator:7",
      "WGS84_Pseudo-Mercator:8",
      "WGS84_Pseudo-Mercator:9",
      "WGS84_Pseudo-Mercator:10",
      "WGS84_Pseudo-Mercator:11",
      "WGS84_Pseudo-Mercator:12",
      "WGS84_Pseudo-Mercator:13",
      "WGS84_Pseudo-Mercator:14",
      "WGS84_Pseudo-Mercator:15",
    ],
    tileSize: 256,
    extent: [
      2126783.875006739, 8321240.304448184, 3103954.8446044326,
      9835304.960720954,
    ],
  }),
  style: "",
  transition: 0,
};

function useCreateMapLayers(): UseCreateMapLayersReturn {
  const api = useThirdPartyApi();
  const apiErrorHandler = useApiErrorHandler();
  const { seaToFeatures, waveToFeatures, windToFeatures } =
    useWeatherLayerHelpers();
  const { accesses } = useAccess();
  const { getToken } = useAuth();
  const createMapLayers = useCallback(
    (includedLayers?: LayerTitle[]): LayerGroup[] => {
      // Each layer has a title
      // Title is used as an identifier and as a key for translation
      // Group is used for dividing the layers to different settings groups

      // OpenStreetMap
      const OSM = createLayer({
        source: new OSMSource(),
        layer: new TileLayer(),
        title: LayerTitle.OSM,
      });

      // S57 from traficom
      const S57 = createLayer({
        source: new TileWMS({
          url: "https://julkinen.traficom.fi/s57/wms",
          attributions: ["Traficom CC 4.0  BY."],
          params: {
            LAYERS: "cells",
            STYLES: "style-id-201",
          },
          serverType: "geoserver",
          transition: 0,
        }),
        layer: new TileLayer(),
        title: LayerTitle.S57,
      });

      // SeaMaps from traficom
      const SeaMap = createLayer({
        source: new WMTS({
          ...shoreWmtsOptions,
          layer: "Traficom:Merikarttasarjat public",
        }),
        layer: new TileLayer(),
        title: LayerTitle.SeaMap,
      });

      // SeaMaps from traficom
      const ShoreMap = createLayer({
        source: new WMTS(shoreWmtsOptions),
        layer: new TileLayer(),
        title: LayerTitle.ShoreMap,
      });

      const backgroundGroup = createLayerGroup({
        includedLayers,
        layers: [OSM, S57, SeaMap, ShoreMap],
        title: LayerGroupTitle.BackgroundMap,
        accesses,
      });

      // Ports
      const portsGroup = createLayerGroup({
        includedLayers,
        layers: [
          createPortLayer().layer,
          createBerthLayer().layer,
          createPortServiceLayer().layer,
          createArrivalZoneLayer().layer,
        ],
        title: LayerGroupTitle.Ports,
        accesses,
      });

      // Events and warnings
      const eventsGroup = createLayerGroup({
        includedLayers,
        layers: [createEventLayer().layer, createNauticalWarningLayer().layer],
        title: LayerGroupTitle.Events,
        accesses,
      });

      const vaylaAPI = (name: string, extent: Extent) => {
        return `https://julkinen.vayla.fi/inspirepalvelu/avoin/wfs?request=GetFeature&typeName=avoin:${name}&outputFormat=application/json&srsname=EPSG:3857&bbox=${extent.join(
          ",",
        )},EPSG:3857`;
      };

      // Navigation lines
      const navigationLayer = createLayer({
        source: new VectorSource({
          format: new GeoJSON(),
          url: (extent) => vaylaAPI("navigointilinjat", extent),
          attributions: [vaylaAttributions],
          strategy: bboxStrategy,
        }),
        layer: new VectorLayer({
          style: new Style({
            stroke: new Stroke({
              color: "rgba(0, 0, 0, 0.8)",
              width: 0.8,
            }),
          }),
        }),
        title: LayerTitle.NavigationLines,
      });

      // Leading lines
      const leadingLayer = createLayer({
        source: new VectorSource({
          format: new GeoJSON(),
          url: (extent) => vaylaAPI("taululinja", extent),
          strategy: bboxStrategy,
          attributions: [vaylaAttributions],
        }),
        layer: new VectorLayer({
          style: new Style({
            stroke: new Stroke({
              color: "rgba(100, 100, 100, 1.0)",
              width: 1,
              lineDash: [20, 5],
            }),
          }),
        }),
        title: LayerTitle.LeadingLines,
      });

      const waterwaysGroup = createLayerGroup({
        includedLayers,
        layers: [navigationLayer, leadingLayer, createFairwayLayer().layer],
        title: LayerGroupTitle.Waterways,
        accesses,
      });

      // Vessels
      const vesselsLayer = createAISVesselLayer();
      const vesselsAndEquipmentGroup = createLayerGroup({
        includedLayers,
        layers: [
          vesselsLayer,
          createVehicleEquipmentsLayer().layer,
          // createPTOPCarLayer().layer,
          // createPTOPTrailerLayer().layer,
        ],
        title: LayerGroupTitle.VesselsAndEquipment,
        accesses,
      });

      // Fleets
      const fleetsLayer = createAISVesselLayer(true);
      const fleetsGroup = createLayerGroup({
        includedLayers,
        layers: [
          fleetsLayer,
          // createPTOPCarLayer(true).layer,
          // createPTOPTrailerLayer(true).layer,
        ],
        title: LayerGroupTitle.Fleets,
        accesses,
      });

      // Weather and conditions

      // Wind
      const windStyleCache: {
        [key: string]: Style;
      } = {};

      const windSource = new VectorSource({
        loader: () => {
          api
            .getWeather({
              storedquery_id: "fmi::observations::weather::timevaluepair",
              bbox: `${EXTENT.join(",")},EPSG:3857`,
              crs: "EPSG:3857",
              parameters: `${ObservableProperties.WindSpeed},${ObservableProperties.WindGust},${ObservableProperties.WindDirection}`,
            })
            .then((data) => {
              if (!data) return;
              windSource.addFeatures(windToFeatures(data));
            })
            .catch(apiErrorHandler);
        },
        attributions: [fmiAttributions],
        strategy: allStrategy,
      });

      const windLayer = createLayer({
        source: new Cluster({
          distance: 75,
          source: windSource,
          attributions: [fmiAttributions],
        }),
        layer: new VectorLayer({
          style(feature) {
            const feats = feature.get("features") as FeatureLike[];
            let val = 0;
            let unit = "";
            let dir: number | undefined;
            let highestVal = 0;
            feats.forEach((f) => {
              const p = f.getProperties() as WindMapItem;
              const curVal = p.latestSpeedValue?.value ?? 0;
              val += curVal;
              unit = p.latestSpeedValue?.unit ?? "";

              // Wind direction is the direction of the strongest wind
              if (curVal > highestVal) {
                dir = p.latestDirectionValue?.value;
                highestVal = curVal;
              }
            });
            val /= feats.length;

            const cacheKey = `${val}${dir}`;

            let style = windStyleCache[cacheKey];
            const icon = `${window.location.origin}${windIcon}`;
            if (!style) {
              style = new Style({
                image: new Icon({
                  opacity: 1,
                  src: icon,
                  scale: 1,
                  offset: [0, 0],
                  rotation: dir ? dir * (Math.PI / 180) : undefined,
                }),
                text: new Text({
                  text: unit ? `${val.toFixed(1)} ${unit}` : "",
                  textBaseline: "top",
                  offsetY: 12,
                  fill: new Fill({
                    color: "#eee",
                  }),
                  font: "16px Roboto",
                  stroke: new Stroke({
                    color: "#111",
                    width: 3,
                  }),
                }),
              });
              windStyleCache[cacheKey] = style;
            }
            return style;
          },
        }),
        title: LayerTitle.Wind,
      });

      // Sea level
      const seaStyleCache: {
        [key: number]: Style;
      } = {};

      const seaLevelSource = new VectorSource({
        loader: () => {
          api
            .getSea({
              storedquery_id: "fmi::observations::mareograph::timevaluepair",
              bbox: `${EXTENT.join(",")},EPSG:3857`,
              crs: "EPSG:3857",
            })
            .then((data) => {
              if (!data) return;
              seaLevelSource.addFeatures(seaToFeatures(data));
            })
            .catch(apiErrorHandler);
        },
        attributions: [fmiAttributions],
        strategy: allStrategy,
      });

      const seaLevelLayer = createLayer({
        source: new Cluster({
          distance: 50,
          source: seaLevelSource,
          attributions: [fmiAttributions],
        }),
        layer: new VectorLayer({
          style(feature) {
            const feats = feature.get("features") as FeatureLike[];
            let val = 0;
            let unit = "";
            feats.forEach((f) => {
              const p = f.getProperties() as SeaMapItem;
              val = (p.latestLevelValue?.value ?? 0) + val;
              unit = p.latestLevelValue?.unit ?? "";
            });
            val /= feats.length;
            let style = seaStyleCache[val];
            const icon = `${window.location.origin}${oceanIcon}`;
            if (!style) {
              style = new Style({
                image: new Icon({
                  opacity: 1,
                  src: icon,
                  scale: 1,
                  offset: [0, 0],
                }),
                text: new Text({
                  text: unit ? `${val.toFixed(0)} ${unit}` : "",
                  textBaseline: "top",
                  offsetY: 12,
                  fill: new Fill({
                    color: "#eee",
                  }),
                  font: "16px Roboto",
                  stroke: new Stroke({
                    color: "#111",
                    width: 3,
                  }),
                }),
              });
              seaStyleCache[val] = style;
            }
            return style;
          },
        }),
        title: LayerTitle.SeaLevel,
      });

      // Wave height
      const waveHeightSource = new VectorSource({
        loader: () => {
          api
            .getWave({
              storedquery_id: "fmi::observations::wave::timevaluepair",
              bbox: `${EXTENT.join(",")},EPSG:3857`,
              crs: "EPSG:3857",
              parameters: `${ObservableProperties.WaveHeight},${ObservableProperties.WaveDirection}`,
            })
            .then((data) => {
              if (!data) return;
              waveHeightSource.addFeatures(waveToFeatures(data));
            })
            .catch(apiErrorHandler);
        },
        attributions: [fmiAttributions],
        strategy: allStrategy,
      });

      const waveHeightLayer = createLayer({
        source: waveHeightSource,
        layer: new VectorLayer({
          style(feature) {
            const icon = `${window.location.origin}${wavesIcon}`;
            const props = feature.getProperties() as WaveMapItem;
            const unit = props.latestHeightValue?.unit;
            const val = props.latestHeightValue?.value;
            return new Style({
              image: new Icon({
                opacity: 1,
                src: icon,
                color: "#fff",
                scale: 1,
                offset: [0, 0],
              }),
              text: new Text({
                text: unit && val ? `${val.toFixed(1)} ${unit}` : "",
                textBaseline: "top",
                offsetY: 12,
                fill: new Fill({
                  color: "#eee",
                }),
                font: "16px Roboto",
                stroke: new Stroke({
                  color: "#111",
                  width: 3,
                }),
              }),
            });
          },
        }),
        title: LayerTitle.WaveHeight,
      });
      // getIce().then((d) => {
      //   console.log("call getice");
      //   console.log(d.data);
      //   console.log(URL.createObjectURL(d.data));
      // });
      const weatherAndConditions = createLayerGroup({
        includedLayers,
        layers: [
          windLayer,
          seaLevelLayer,
          waveHeightLayer,
          createLuodeLayer().layer,
          createIceLayer(getToken).layers,
        ],
        title: LayerGroupTitle.WeatherAndConditions,
        accesses,
      });

      // Measurements
      const measurementsGroup = createLayerGroup({
        includedLayers,
        layers: [
          createMeasurementsLayer().layer,
          createProjectLayer().layer,
          createProjectPlanningLayer().layer,
          createProjectPoiItemLayer().layer,
        ],
        title: LayerGroupTitle.Measurements,
        accesses,
      });

      // Dams and ferries
      const DamsAndFerries = createLayerGroup({
        includedLayers,
        layers: [createDamLayer().layer, createFerryLayer().layer],
        title: LayerGroupTitle.DamsAndFerries,
        accesses,
      });

      // AtoNs
      const AtoNsGroup = createLayerGroup({
        includedLayers,
        layers: [createAtoNLayer().layer],
        title: LayerGroupTitle.AtoNs,
        accesses,
      });

      // Trackers
      const TrackersGroup = createLayerGroup({
        includedLayers,
        layers: [
          createYepzonTrackerLayer().layer,
          createTaivalTrackerLayer().layer,
        ],
        title: LayerGroupTitle.TrackerDevices,
        accesses,
      });

      const groups = [
        backgroundGroup,
        portsGroup,
        eventsGroup,
        vesselsAndEquipmentGroup,
        fleetsGroup,
        waterwaysGroup,
        weatherAndConditions,
        measurementsGroup,
        DamsAndFerries,
        AtoNsGroup,
        TrackersGroup,
      ];

      return groups.filter((g) => g !== null) as LayerGroup[];
    },
    [
      accesses,
      api,
      apiErrorHandler,
      getToken,
      seaToFeatures,
      waveToFeatures,
      windToFeatures,
    ],
  );

  return { createMapLayers };
}

export default useCreateMapLayers;
